import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { useRef, useState } from 'preact/hooks';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { BtnPrimary } from '@components/buttons';
import { StripeContext, StripeInput } from 'client/courses/components/checkout/stripe';
import { processStripePayment } from 'client/courses/components/checkout/stripe-checkout-form';
import {
  describeCheckoutError,
  loggableCheckoutError,
} from 'client/courses/components/checkout/checkout-logic';
import { reportCheckoutError } from '@course/components/checkout/report-checkout-error';
import { showError } from '@components/app-error';
import { useCurrentUser } from 'client/lib/auth';

dayjs.extend(utc);

type Transfer = Extract<
  RpxResponse<typeof rpx.aipBillingTransfer.getMyAipBillingTransfer>,
  { kind: 'pending' }
>;

function formatCharge(firstChargeOn: string) {
  return dayjs.utc(`${firstChargeOn}T00:00:00Z`).format('MMMM D, YYYY');
}

function formatAmount(cents: number) {
  const dollars = cents / 100;
  return `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`;
}

// Read the cadence off the price rather than assuming monthly — the legacy set
// includes yearly members, and telling one of them "/month" would misstate what
// they are agreeing to.
function cadence(interval?: string) {
  if (interval === 'year') {
    return '/year';
  }
  if (interval === 'month') {
    return '/month';
  }
  return '';
}

/**
 * Card entry for a legacy member moving onto app billing. Rendered in place of
 * the read-only Ontraport card's "email us" body once ops has issued the
 * transfer, so the member acts where they already went looking for billing.
 */
export function AipBillingTransferForm({
  transfer,
  onDone,
}: {
  transfer: Transfer;
  onDone(): void;
}) {
  const user = useCurrentUser()!;
  const [stripeCtx, setStripeCtx] = useState<StripeContext | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState<string | undefined>(undefined);
  const processingRef = useRef(false);

  async function handleSubmit() {
    if (!stripeCtx || processingRef.current) {
      return;
    }
    processingRef.current = true;
    setIsProcessing(true);
    setCardError(undefined);
    let succeeded = false;
    try {
      const result = await processStripePayment({
        ctx: stripeCtx,
        user,
        priceId: transfer.priceId,
        fallbackAddress: transfer.savedAddress,
        processPayment: async (opts) => {
          return rpx.aipBillingTransfer.payAipBillingTransfer({ address: opts.address });
        },
      });
      if (!result) {
        setCardError('We could not save your card. Please try again.');
        return;
      }
      if (result.type === 'validationError') {
        // Stripe marks its own incomplete fields, but an incomplete Address
        // Element shows nothing — leaving a Save click that visibly does
        // nothing at all. Say what is missing rather than resetting the button.
        setCardError('Please fill in every field above, then try again.');
        return;
      }
      succeeded = true;
      onDone();
    } catch (err) {
      const described = describeCheckoutError(err);
      reportCheckoutError({ step: 'checkout', priceId: transfer.priceId, err });
      if (described.kind === 'card') {
        setCardError(described.message);
        console.warn('aip billing transfer card error', loggableCheckoutError(err));
      } else {
        showError(err);
      }
    } finally {
      if (!succeeded) {
        processingRef.current = false;
        setIsProcessing(false);
      }
    }
  }

  return (
    <div class="flex flex-col gap-3">
      <p class="text-sm text-gray-700">
        Add a card to move your AI Playground membership to this site. First charge{' '}
        {formatCharge(transfer.firstChargeOn)}, {formatAmount(transfer.priceInCents)}
        {cadence(transfer.interval)}. Your access does not change, and we will cancel your Ontraport
        billing.
      </p>
      <StripeInput
        priceId={transfer.priceId}
        customerName={user.name}
        onReady={setStripeCtx}
        collectAddress={transfer.requiresAddress}
        disabled={isProcessing}
        currency={transfer.currency}
        // Always 'setup', never 'subscription': the transfer is always issued at
        // least MIN_DAYS_AHEAD out, so `payAipBillingTransfer` always creates a
        // TRIALING subscription. That bills a $0 first invoice and hands back a
        // pending_setup_intent, so the client confirms with stripe.confirmSetup
        // — which rejects an Element built in subscription mode (automatic
        // payment methods vs. the intent's explicit ['card']). Setup mode pins
        // ['card'] to match, as every other trial checkout form here does.
        mode="setup"
      />
      {cardError && (
        <div
          role="alert"
          class="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {cardError}
        </div>
      )}
      <BtnPrimary class="p-3" onClick={handleSubmit} disabled={isProcessing || !stripeCtx}>
        {isProcessing ? 'Saving…' : 'Save card'}
      </BtnPrimary>
    </div>
  );
}
